import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Grid,
    Box,
    Card,
    Typography,
    Checkbox,
    IconButton,
    Snackbar,
    Alert,
    CircularProgress,
    CardContent
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import logo from "assets/images/logo.png";
import { useTranslation } from "react-i18next";
import TermsAndConditionsDialog from "./TermsAndConditionsDialog";
import { register } from "../../../axios-client";
import LanguageSwitcher from "../../../reusableComponents/LanguageSwitcher/LanguageSwitcher";
import {useTheme} from "@mui/material/styles";
import {setLayout} from "../../../store/actions/app-actions";
import {useSoftUIController} from "../../../context";
import background from "../../../assets/images/curved-images/background.png";


function SignUp() {
    const theme = useTheme();

    const [agreement, setAgreement] = useState(true);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        university: "",
        department: [],
        agreement: "",
        isFreeAgent: true
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "" });
    const [isActivating, setIsActivating] = useState(false);
    const [openTermsDialog, setOpenTermsDialog] = useState(false);
    const [controller, dispatch] = useSoftUIController();
    const navigate = useNavigate();
    const [animateLogo, setAnimateLogo] = useState(false);

    useEffect(() => {
        setTimeout(() => setAnimateLogo(true), 200);
        setLayout(dispatch, "page");
    }, []);

    const {i18n} = useTranslation();
    const { t } = useTranslation(['signUp']);
    const dir = i18n.dir();
    const currentLang = i18n.language;

    const signInUrl = `/${currentLang}/authentication/sign-in`;

    const features = t('panel.features', { returnObjects: true });

    const handleTermsOpen = () => {
        setOpenTermsDialog(true);
    };

    const handleTermsClose = () => {
        setOpenTermsDialog(false);
    };


    const handleSetAgreement = () => {
        setAgreement(!agreement);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };


    const handleSubmit =  (e) => {
        e.preventDefault();
        if (validateForm()) {
            setIsActivating(true);
            const updatedFormData = {
                ...formData,
                language: currentLang,
            };

            register(updatedFormData)
                .then((response) => {
                    setSnackbar({
                        open: true,
                        message: t("signUp.registrationSuccess"),
                        severity: "success",
                    });
                    return new Promise(resolve => setTimeout(resolve, 1000));
                })
                .then(() => {
                    navigate(`/${currentLang}/authentication/confirmation`, { state:  "free-agent"  });
                })
                .catch((error) => {
                    return new Promise(resolve => setTimeout(resolve, 2000))
                        .then(() => handleRegistrationError(error));
                })
                .finally(() => {
                    setIsActivating(false);
                });
        }
    };

    const handleRegistrationError = (error) => {
        if (error.response && error.response.status === 400 && error.response.data.message === "Email already exists") {
            setSnackbar({
                open: true,
                message: t("signUp.errors.emailRegistered"),
                severity: "error",
            });
        } else {
            setSnackbar({
                    open: true,
                    message: t("signUp.errors.registration"),
                    severity: "error",
                }
            );}
        setIsActivating(false);
    };


    const validateForm = () => {
        const newErrors = {};
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailPattern.test(formData.email)) {
            newErrors.email = t("signUp.errors.validEmail");
        }
        if (formData.password && formData.password.length < 8) {
            newErrors.password = t("signUp.errors.passwordLength");
        } else if (formData.password && !/[!@#$%^&*]/.test(formData.password)) {
            newErrors.password = t("signUp.errors.passwordCharacters");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSnackbarClose = () => {
        setSnackbar({ open: false, message: "", severity: "" });
    };


    return (
        <>

            <Box
                sx={{
                    minHeight: "100vh",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                }}
            >
                {/* Background Image with Brightness Filter */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: `url(${background})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        filter: "brightness(1.8) contrast(1.2) saturate(1.1)",
                    }}
                />

                {/* Light Overlay to Further Brighten */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        top: theme.spacing(2),
                        left: theme.spacing(2),
                        right: theme.spacing(2),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        zIndex: 10,
                    }}
                >
                    <Box
                        sx={{
                            opacity: animateLogo ? 1 : 0,
                            transform: animateLogo ? "translateY(0)" : "translateY(-10px)",
                            transition: "all 0.8s ease",
                        }}

                    >
                        <img src={logo} alt="YaiGlobal Logo" style={{height: 48}}/>
                    </Box>

                    <LanguageSwitcher color={"white"}/>
                </Box>

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 10,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                        maxWidth: 400,
                    }}
                >

                    {/* Login Form */}
                    <Card
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            width: "100%",
                            maxWidth: 400,
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            backdropFilter: "blur(8px)",
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                            border: "none",
                        }}
                    >
                        <CardContent sx={{p: 4}}>
                            {/* Header */}
                            <Box sx={{textAlign: "center", mb: 3}}>
                                <Typography
                                    variant="h4"
                                    component="h1"
                                    sx={{
                                        fontWeight: "bold",
                                        color: "#111827",
                                        mb: 1,
                                    }}
                                >
                                    {t("signUp.createAccount")}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#6B7280",
                                    }}
                                >
                                    {t("signUp.description")}
                                </Typography>
                            </Box>

                            {/* Form */}

                            <Box component="form" onSubmit={handleSubmit}
                                 sx={{display: "flex", flexDirection: "column", gap: 1}}>

                                <SoftBox mb={2}>
                                    <SoftInput
                                        type="text"
                                        placeholder={t("signUp.name")}
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </SoftBox>

                                <SoftBox mb={2}>
                                    <SoftInput
                                        type="email"
                                        placeholder={t("signUp.email")}
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </SoftBox>


                                <SoftBox mb={2} position="relative">
                                    <SoftInput
                                        type={showPassword ? "text" : "password"}
                                        placeholder={t("signUp.password")}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                    />
                                    <IconButton
                                        onClick={togglePasswordVisibility}
                                        sx={{
                                            position: "absolute",  ...(i18n.language === "ar" ? {left:10} : {right:10}), top: "50%", transform: "translateY(-50%)",
                                            marginRight: currentLang == 'ar' ? "205px" : "0px"
                                        }}
                                    >
                                        {showPassword ? <VisibilityOff/> : <Visibility/>}
                                    </IconButton>
                                    {errors.password && (
                                        <SoftTypography color="error"
                                                        sx={{fontSize: "0.75rem"}}>{errors.password}</SoftTypography>
                                    )}
                                </SoftBox>

                                <SoftBox display="flex" alignItems="center" mt={1}>
                                    <Checkbox checked={agreement} onChange={handleSetAgreement}
                                              required={true}
                                    />
                                    <Typography variant="caption">
                                        {t("signUp.agreement")}{" "}
                                        <span onClick={handleTermsOpen} style={{color: "#cb0c9f", cursor: "pointer"}}>
                                          {t("signUp.terms")}
                                        </span>
                                    </Typography>
                                </SoftBox>
                                {errors.agreement && <SoftTypography color="error">{errors.agreement}</SoftTypography>}

                                <SoftBox mt={3}>
                                    <SoftButton variant="gradient" color="primary" fullWidth type="submit">
                                        {isActivating ? t("signUp.pleaseWait") : t("signUp.signUp")}
                                        {isActivating && <CircularProgress size={20} sx={{ml: 1, color: "white"}}/>}
                                    </SoftButton>
                                </SoftBox>

                                <SoftBox mt={3} textAlign="center">
                                    <Typography variant="caption">
                                        {t("signUp.alreadyHasAccount")}{" "}
                                        <Link to={signInUrl} style={{color: "#cb0c9f", fontWeight: 500}}>
                                            {t("signUp.signIn")}
                                        </Link>
                                    </Typography>
                                </SoftBox>
                            </Box>
                        </CardContent>

                    </Card>
                </Box>
            </Box>

            <TermsAndConditionsDialog open={openTermsDialog} close={handleTermsClose}/>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </>

    );
}

export default SignUp;